import React from "react";

import type { AddressWithdrawalsItem } from "types/api/address";
import type { BlockWithdrawalsItem } from "types/api/block";
import type { WithdrawalsItem } from "types/api/withdrawals";

import config from "configs/app";
import { currencyUnits } from "lib/units";
import { Skeleton } from "toolkit/chakra/skeleton";
import CurrencyValue from "ui/shared/CurrencyValue";
import AddressEntity from "ui/shared/entities/address/AddressEntity";
import BlockEntity from "ui/shared/entities/block/BlockEntity";
import ListItemMobileGrid from "ui/shared/ListItemMobile/ListItemMobileGrid";
import TimeWithTooltip from "ui/shared/time/TimeWithTooltip";

const feature = config.features.beaconChain;

type Props = (
  | {
      item: WithdrawalsItem;
      view: "list";
    }
  | {
      item: AddressWithdrawalsItem;
      view: "address";
    }
  | {
      item: BlockWithdrawalsItem;
      view: "block";
    }
) & { isLoading?: boolean };

const BeaconChainWithdrawalsListItem = ({ item, isLoading, view }: Props) => {
  if (!feature.isEnabled) {
    return null;
  }

  return (
    <ListItemMobileGrid.Container gridTemplateColumns="100px auto">
      <ListItemMobileGrid.Label isLoading={isLoading}>
        Index
      </ListItemMobileGrid.Label>
      <ListItemMobileGrid.Value>
        <Skeleton loading={isLoading} display="inline-block">
          {item.index}
        </Skeleton>
      </ListItemMobileGrid.Value>

      <ListItemMobileGrid.Label isLoading={isLoading}>
        Validator index
      </ListItemMobileGrid.Label>
      <ListItemMobileGrid.Value>
        <Skeleton loading={isLoading} display="inline-block">
          {item.validator_index}
        </Skeleton>
      </ListItemMobileGrid.Value>

      {view !== "block" && (
        <>
          <ListItemMobileGrid.Label isLoading={isLoading}>
            Block
          </ListItemMobileGrid.Label>
          <ListItemMobileGrid.Value>
            <BlockEntity
              number={item.block_number}
              isLoading={isLoading}
              textStyle="sm"
            />
          </ListItemMobileGrid.Value>
        </>
      )}

      {view !== "address" && (
        <>
          <ListItemMobileGrid.Label isLoading={isLoading}>
            To
          </ListItemMobileGrid.Label>
          <ListItemMobileGrid.Value>
            <AddressEntity
              address={item.receiver}
              isLoading={isLoading}
              truncation="constant"
            />
          </ListItemMobileGrid.Value>
        </>
      )}

      {view !== "block" && (
        <>
          <ListItemMobileGrid.Label isLoading={isLoading}>
            Age
          </ListItemMobileGrid.Label>
          <ListItemMobileGrid.Value>
            <TimeWithTooltip
              timestamp={item.timestamp}
              isLoading={isLoading}
              display="inline-block"
            />
          </ListItemMobileGrid.Value>

          <ListItemMobileGrid.Label isLoading={isLoading}>
            Value
          </ListItemMobileGrid.Label>
          <ListItemMobileGrid.Value>
            <CurrencyValue
              value={item.amount}
              currency={currencyUnits.ether}
              isLoading={isLoading}
            />
          </ListItemMobileGrid.Value>
        </>
      )}
    </ListItemMobileGrid.Container>
  );
};

export default BeaconChainWithdrawalsListItem;
